import React, { useState } from "react";
import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Grid from "@material-ui/core/Grid";
import { ReactComponent as DeleteSlotIcon } from "../../../assets/svg/DeleteIcon.svg";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RemoveButton from "components/common/RemoveButton/RemoveButton";
import LinkButton from "../../../common/LinkButton/LinkButton";
import { isEmptyOrNull } from "utils/DataUtils";
import { ALL_DAYS } from "../../../../config/constants";
import { generateDropdownItems } from "../../../../utils/helperFunctions";
import MultiSelectBox from "../../../common/MultiSelectBox/MultiSelectBox";
import PrimaryButton from "../../../common/PrimaryButton/PrimaryButton";
import SelectBox from "../../../common/SelectBox/SelectBox";
import styles from "./RangeGeneratorFormStyles";
import SlotRange from "./SlotRange";
import TimePicker from "../../../common/TimePicker/TimePicker";
import { Typography } from "@material-ui/core";
import { TEXT_CONSTANT } from "../DefaultInterface";
import PriorityFeeField from "../../../common/PriorityFeeField/PriorityFeeField";

const MINIMUM_DAYS = generateDropdownItems(0, 10, "days");
const MAXIMUM_DAYS = generateDropdownItems(0, 20, "days");

interface SlotFormData {
  minimumDays: any;
  maximumDays: any;
  cutOff: string | null;
  priorityFee: any;
}

interface RangeGeneratorFormProps {
  tabIndex: number;
  formIndex: number;
  days: any;
  remainingDays: any;
  minimumDays: any;
  priorityFee: any;
  maximumDays: any;
  bufferTimeHours: any;
  defaultSlotCapacity: any;
  generatedRange: any;
  onDaysChange: any;
  onGenerateRangeSubmit: any;
  onIndividualRangeFieldsChange: any;
  handleResetSlotsClick: any;
  objectKey: any;
  deleteConfigureSlotsOption: any;
  generatorFormIndex: any;
  configTabIndex: any;
  cutOff: string | null;
  onIndividualCutOffFieldsChange: any;
}

const RangeGeneratorForm = (props: RangeGeneratorFormProps) => {
  const {
    tabIndex,
    formIndex,
    days,
    remainingDays,
    minimumDays,
    maximumDays,
    generatedRange = [],
    onDaysChange,
    onGenerateRangeSubmit,
    onIndividualRangeFieldsChange,
    handleResetSlotsClick,
    objectKey,
    deleteConfigureSlotsOption,
    generatorFormIndex,
    configTabIndex,
    cutOff,
    onIndividualCutOffFieldsChange,
    priorityFee,
  } = props;

  const classes = styles();

  const [slotForms, setSlotForms] = useState<SlotFormData[]>([
    { minimumDays: minimumDays ?? "", maximumDays: maximumDays ?? "", cutOff: cutOff ?? null, priorityFee: priorityFee ?? "" },
  ]);

  const handleSlotFormChange = (localIdx: number, event: any) => {
    const { name, value } = event.target;
    setSlotForms((prev) => prev.map((f, i) => (i === localIdx ? { ...f, [name]: value } : f)));
  };

  const handleSlotFormCutOffChange = (localIdx: number, date: any) => {
    setSlotForms((prev) => prev.map((f, i) => (i === localIdx ? { ...f, cutOff: date } : f)));
  };

  const handleAddNewSlot = () => {
    setSlotForms((prev) => [...prev, { minimumDays: "", maximumDays: "", cutOff: null, priorityFee: "" }]);
  };

  const handleDeleteSlot = (localIdx: number) => {
    setSlotForms((prev) => prev.filter((_, i) => i !== localIdx));
  };

  const handleResetAll = () => {
    setSlotForms([{ minimumDays: "", maximumDays: "", cutOff: null, priorityFee: "" }]);
    handleResetSlotsClick(tabIndex, formIndex);
  };

  const handleGenerateAll = () => {
    onGenerateRangeSubmit(tabIndex, formIndex, objectKey, slotForms);
  };

  const isAllFormsValid = () =>
    slotForms.every(
      (f) => !isEmptyOrNull(f.minimumDays) && !isEmptyOrNull(f.maximumDays) && !isEmptyOrNull(f.cutOff) && !isEmptyOrNull(f.priorityFee)
    );

  const renderDaysInput = () => {
    return (
      <>
        <div className={classes.fieldLabel}>Days</div>
        <MultiSelectBox menuitems={remainingDays} value={days} onChange={(e: any) => onDaysChange(tabIndex, formIndex, e, objectKey)} />
      </>
    );
  };

  const renderGeneratedRange = () => {
    return (
      <div>
        <div className={classes.separation} />
        <Grid container>
          <Grid className={classes.fontSize16}>
            {`Generated Slots for : `}
            {<span className={classes.slotDays}>{`${days.length >= 7 ? ALL_DAYS : days.join(", ").toLowerCase()}`}</span>}
          </Grid>
        </Grid>

        <Grid container spacing={1} className={classes.generatedSlotsTable}>
          <Grid item xs={3} className={classes.slotsTableLabel}>
            Days Range
          </Grid>
          <Grid item xs={3} className={classes.slotsTableLabel}>
            {`${TEXT_CONSTANT.CUT_OFF}`}
          </Grid>
          <Grid item xs={3} className={classes.slotsTableLabel}>
            {`${TEXT_CONSTANT.PRIORITY_FEE}`}
          </Grid>
        </Grid>

        <div>
          {generatedRange.map((range: any, index: number) => (
            <SlotRange
              key={`${range.dateRange}-${index}`}
              tabIndex={tabIndex}
              formIndex={formIndex}
              rangeIndex={index}
              dateRange={range.dateRange}
              capacity={range.capacity}
              bufferTime={range.bufferTime}
              onIndividualRangeFieldsChange={onIndividualRangeFieldsChange}
              minimumDays={range.minimumDays}
              maximumDays={range.maximumDays}
              cutOff={range.cutOff}
              priorityFee={range.priorityFee}
              onIndividualCutOffFieldsChange={onIndividualCutOffFieldsChange}
              objectKey={objectKey}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderRangeGeneratorForm = (form: SlotFormData, localIdx: number) => {
    return (
      <Grid container spacing={1} key={localIdx} alignItems="flex-end">
        <Grid item xs={2}>
          <SelectBox
            fieldLabel={localIdx === 0 ? `${TEXT_CONSTANT.MINIMUM_DAYS}` : undefined}
            name="minimumDays"
            menuItems={MINIMUM_DAYS}
            value={form.minimumDays}
            handleChange={(e: any) => handleSlotFormChange(localIdx, e)}
          />
        </Grid>
        <Grid item xs={2}>
          <SelectBox
            fieldLabel={localIdx === 0 ? `${TEXT_CONSTANT.MAXIMUM_DAYS}` : undefined}
            name="maximumDays"
            menuItems={MAXIMUM_DAYS}
            value={form.maximumDays}
            handleChange={(e: any) => handleSlotFormChange(localIdx, e)}
          />
        </Grid>
        <Grid item xs={3}>
          {localIdx === 0 && <Typography className={classes.rangeHeaderSpanStyle}>{`${TEXT_CONSTANT.CUT_OFF}`}</Typography>}
          <TimePicker
            value={form.cutOff}
            handleChange={(date: any) => handleSlotFormCutOffChange(localIdx, date)}
            disabled={false}
            fieldLabel={undefined}
            className={undefined}
            timerProps={undefined}
          />
        </Grid>
        <Grid item xs={2}>
          {localIdx === 0 && <Typography className={classes.rangeHeaderSpanStyle}>{`${TEXT_CONSTANT.PRIORITY_FEE}`}</Typography>}
          <PriorityFeeField
            value={form.priorityFee}
            onChange={(e: any) => handleSlotFormChange(localIdx, e)}
          />
        </Grid>
        {localIdx > 0 && (<Grid item xs={2}>
          <DeleteSlotIcon className={classes.iconButton} onClick={() => handleDeleteSlot(localIdx)} />
        </Grid>)}
      </Grid>
    );
  };

  return (
    <div className={classes.slotsGeneratorForm}>
      <Accordion defaultExpanded={true} className={classes.whiteBackground}>
        <AccordionSummary className={classes.slotsGeneratorAccordionSummary} expandIcon={<ExpandMoreIcon />} classes={{ expanded: classes.expandedStyle }}>
          <Grid container>
            <Grid item xs={6} className={classes.daysInput}>
              {renderDaysInput()}
            </Grid>
          </Grid>
          {generatorFormIndex !== 0 && <RemoveButton onClick={() => deleteConfigureSlotsOption(configTabIndex, objectKey, generatorFormIndex)} />}
        </AccordionSummary>
        <AccordionDetails className={classes.slotsGeneratorAccordionDetails}>
          {slotForms.map((form, localIdx) => renderRangeGeneratorForm(form, localIdx))}
          <Grid item xs={12}>
            <LinkButton buttonLabel="Add New Range" onClick={handleAddNewSlot} />
          </Grid>
          <Grid container spacing={3} className={classes.buttons}>

            <Grid container justifyContent="flex-end" style={{ textAlign: "right" }}>
              <Grid item>
                <PrimaryButton buttonLabel="Reset Range" onClick={handleResetAll} />
              </Grid>
              <Grid item className={classes.generateSlotsButton}>
                <PrimaryButton buttonLabel="Generate Range" onClick={handleGenerateAll} disabled={!isAllFormsValid()} />
              </Grid>
            </Grid>
          </Grid>
          {generatedRange.length ? renderGeneratedRange() : null}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default RangeGeneratorForm;
